import React, { useEffect, useMemo, useState } from 'react';
import { AttendanceCalcResponse, AttendanceEstado } from '../services/horarios.service';
import {
  exportReporteRotativosCsv,
  getReporteRotativos,
  getRotativosAgentes,
  getRotativosTurnos,
  RotativoAgente,
  RotativoTurno,
} from '../services/rotativos.service';
import { getErrorMessage } from '../utils/errorHandler';
import { formatTime24 } from '../utils/dateTime';

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIso = (): string => toIsoDate(new Date());

const getCurrentWeekStartIso = (): string => {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - mondayOffset);
  return toIsoDate(monday);
};

const estados: AttendanceEstado[] = ['TODOS', 'OK', 'OBSERVADO', 'AUSENTE', 'JUSTIFICADO', 'FERIADO'];

const toIsoFromDisplay = (value: string): string => {
  const [day, month, year] = String(value).split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
};

const ReporteRotativosPage: React.FC = () => {
  const [agentes, setAgentes] = useState<RotativoAgente[]>([]);
  const [turnos, setTurnos] = useState<RotativoTurno[]>([]);
  const [agenteId, setAgenteId] = useState('');
  const [turnoId, setTurnoId] = useState('');
  const [estado, setEstado] = useState<AttendanceEstado>('TODOS');
  const [desde, setDesde] = useState<string>(() => getCurrentWeekStartIso());
  const [hasta, setHasta] = useState<string>(() => getTodayIso());
  const [data, setData] = useState<AttendanceCalcResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rowsWithCycleMeta = useMemo(() => {
    if (!data) return [];

    type RowMeta = AttendanceCalcResponse['rows'][number] & {
      cambioCiclo: boolean;
      cambioPatron: boolean;
      rangoEfectivo: string;
      isoFecha: string;
      patronCalculado: string;
    };

    const sorted = [...data.rows].sort((a, b) => {
      if (a.userid !== b.userid) return a.userid.localeCompare(b.userid, 'es', { sensitivity: 'base' });
      return toIsoFromDisplay(a.fecha).localeCompare(toIsoFromDisplay(b.fecha));
    });

    const decorated: RowMeta[] = sorted.map((row) => ({
      ...row,
      cambioCiclo: false,
      cambioPatron: false,
      rangoEfectivo: '',
      isoFecha: toIsoFromDisplay(row.fecha),
      patronCalculado: row.patronRotativo || `${row.laborales ?? '-'}x${row.noLaborales ?? '-'}`,
    }));

    let segmentStart = 0;
    for (let i = 1; i <= decorated.length; i += 1) {
      const prev = decorated[i - 1];
      const current = decorated[i];
      const endSegment =
        i === decorated.length ||
        current.userid !== prev.userid ||
        current.ciclo !== prev.ciclo;

      if (!endSegment) continue;

      const segmentRows = decorated.slice(segmentStart, i);
      const firstDate = segmentRows[0]?.fecha || '';
      const lastDate = segmentRows[segmentRows.length - 1]?.fecha || '';
      const rangeLabel = firstDate === lastDate ? firstDate : `${firstDate} - ${lastDate}`;
      for (let j = segmentStart; j < i; j += 1) {
        decorated[j].rangoEfectivo = rangeLabel;
      }

      if (segmentStart > 0) {
        const prevSegmentLast = decorated[segmentStart - 1];
        const segmentFirst = decorated[segmentStart];
        if (segmentFirst && prevSegmentLast && segmentFirst.userid === prevSegmentLast.userid) {
          segmentFirst.cambioCiclo = segmentFirst.ciclo !== prevSegmentLast.ciclo;
        }
      }

      segmentStart = i;
    }

    for (let i = 1; i < decorated.length; i += 1) {
      const prev = decorated[i - 1];
      const current = decorated[i];
      if (current.userid !== prev.userid) continue;

      current.cambioPatron = current.patronCalculado !== prev.patronCalculado;
    }

    return decorated;
  }, [data]);

  useEffect(() => {
    void Promise.all([getRotativosAgentes(), getRotativosTurnos()])
      .then(([agentesResp, turnosResp]) => {
        setAgentes(Array.isArray(agentesResp.data) ? agentesResp.data : []);
        setTurnos(Array.isArray(turnosResp.data) ? turnosResp.data : []);
      })
      .catch((err) => {
        setError(getErrorMessage(err));
      });
  }, []);

  const generar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setData(null);

    try {
      const resp = await getReporteRotativos({
        desde,
        hasta,
        agenteId: agenteId || undefined,
        turnoId: turnoId || undefined,
        estado,
      });
      setData(resp.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportar = async () => {
    try {
      const blob = await exportReporteRotativosCsv({
        desde,
        hasta,
        agenteId: agenteId || undefined,
        turnoId: turnoId || undefined,
        estado,
      });
      const href = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `reporte-rotativos-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <>
      <h1 className="page-title">Reporte Rotativos</h1>

      <div className="card">
        <h2 className="card-title">Filtros</h2>
        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={generar}>
          <div className="filters-grid" style={{ marginBottom: '12px' }}>
            <div className="form-group">
              <label>Desde</label>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Hasta</label>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Agente</label>
              <select value={agenteId} onChange={(e) => setAgenteId(e.target.value)}>
                <option value="">Todos</option>
                {agentes.map((agente) => (
                  <option key={agente._id} value={agente._id}>
                    {agente.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Turno rotativo</label>
              <select value={turnoId} onChange={(e) => setTurnoId(e.target.value)}>
                <option value="">Todos</option>
                {turnos.map((turno) => (
                  <option key={turno._id} value={turno._id}>
                    {turno.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Estado</label>
              <select value={estado} onChange={(e) => setEstado(e.target.value as AttendanceEstado)}>
                {estados.map((estadoItem) => (
                  <option key={estadoItem} value={estadoItem}>
                    {estadoItem}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generando...' : 'Generar reporte'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={exportar}
              disabled={loading || !data || data.rows.length === 0}
            >
              Exportar Excel
            </button>
          </div>
        </form>
      </div>

      {data && (
        <div className="card">
          <div className="form-grid" style={{ marginBottom: '16px' }}>
            <div>
              <strong>Periodo:</strong> {data.meta.periodoDesde} a {data.meta.periodoHasta}
            </div>
            <div>
              <strong>Total filas:</strong> {data.meta.totalFilas}
            </div>
          </div>

          {rowsWithCycleMeta.length === 0 ? (
            <div className="empty-state">No hay datos para los filtros seleccionados.</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>userid</th>
                    <th>Name</th>
                    <th>IDCard</th>
                    <th>fecha</th>
                    <th>hora entrada Turno</th>
                    <th>hora salida Turno</th>
                    <th>hora entrada real</th>
                    <th>hora salida real</th>
                    <th>ausente</th>
                    <th>llegada Tarde</th>
                    <th>Justificacion</th>
                    <th>tipo justificacion</th>
                    <th>feriado</th>
                    <th>tipo feriado</th>
                    <th>dia semana</th>
                    <th>Turno Nocturno</th>
                    <th>Salida temprano</th>
                    <th>minutos trabajados</th>
                    <th>Horas Trabajadas</th>
                    <th>Ciclo</th>
                    <th>Patron</th>
                    <th>Tipo dia</th>
                    <th>Rango efectivo ciclo</th>
                    <th>Cambio ciclo</th>
                    <th>Cambio patron</th>
                    <th>Turno</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsWithCycleMeta.map((row, i) => (
                    <tr key={`${row.userid}-${row.fecha}-${i}`}>
                      <td>{row.userid}</td>
                      <td>{row.name}</td>
                      <td>{row.idcard}</td>
                      <td>{row.fecha}</td>
                      <td>{formatTime24(row.horaEntradaTurno)}</td>
                      <td>{formatTime24(row.horaSalidaTurno)}</td>
                      <td>{row.horaEntradaReal ? formatTime24(row.horaEntradaReal) : '-'}</td>
                      <td>{row.horaSalidaReal ? formatTime24(row.horaSalidaReal) : '-'}</td>
                      <td>{row.ausente}</td>
                      <td>{row.llegadaTarde}</td>
                      <td>{row.justificacion}</td>
                      <td>{row.tipoJustificacion || '-'}</td>
                      <td>{row.feriado}</td>
                      <td>{row.tipoFeriado || '-'}</td>
                      <td>{row.diaSemana}</td>
                      <td>{row.turnoNocturno}</td>
                      <td>{row.salidaTemprano}</td>
                      <td>{row.minutosTrabajados}</td>
                      <td>{row.horasTrabajadas}</td>
                      <td>{row.ciclo}</td>
                      <td>{row.patronCalculado}</td>
                      <td>{row.tipoDia || 'LABORAL'}</td>
                      <td>{row.rangoEfectivo || '-'}</td>
                      <td>{row.cambioCiclo ? 'SI' : '-'}</td>
                      <td>{row.cambioPatron ? 'SI' : '-'}</td>
                      <td>{row.turno}</td>
                      <td>
                        <span
                          className={`badge ${
                            row.estado === 'OK'
                              ? 'badge-success'
                              : row.estado === 'AUSENTE'
                                ? 'badge-danger'
                                : row.estado === 'OBSERVADO'
                                  ? 'badge-warning'
                                  : 'badge-secondary'
                          }`}
                        >
                          {row.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ReporteRotativosPage;
